
// � 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Data from '../Data/WebData';
import * as Enumerations from '../../Enumerations/Enumerations';
import GLBConverter from '../../Utilities/GLBConverter';
import PointViewer from './PointViewer';
import ViewerActions from '../../actions/ViewerActions';
import * as Inovx from '@shared';
class GLBManager {
    private _scene: BABYLON.Scene;
    private _viewer: PointViewer;
    // Blank scene, no camera, no lighting, purely used for loading glbs and getting meshes.
    private _loaderScene: BABYLON.Scene;
    private _glbConverter: GLBConverter;

    private _tempAssemblyCounter: number = 0;
    private _assemblyCreationTemplate: IAssemblyTemplate;
    private _assemblyRotation: number = 0;
    private _tempAssembly: BABYLON.TransformNode;
    //private _gizmoManager: BABYLON.GizmoManager;
    private _positionGizmo: BABYLON.PositionGizmo;
    private _rotationGizmo: BABYLON.RotationGizmo;
    private _scaleGizmo: BABYLON.BoundingBoxGizmo;
    private _currentAssemblyRotation: BABYLON.Vector3;
    private _assemblySelectionMaterial: BABYLON.StandardMaterial;
    private _assemblyColorMaterials: Map<number, BABYLON.StandardMaterial> = new Map();
    private _selectedGLBKey: [string, string];
    private _pointerMoved = false;
    private _lastPointerPosition: BABYLON.Vector2 | null = null;
    private _moveThreshold: number = 5; // Threshold distance in pixels to consider a move significant.
    private _currentCursorStyle: string = "auto"; // Add a property to store the current cursor style
    private _canvasMouseLeaveHandler: (event: MouseEvent) => void;
    private _tempAssemblyLock: boolean = false;
    private _selectedMesh: { original: BABYLON.TransformNode, selection: BABYLON.TransformNode };
    public _glbAssemblies: { [assemblyID: string]: BABYLON.Mesh[]; } = {};
    public _graphicSources: Map<string, BABYLON.TransformNode> = new Map();
    public _glbAssemblyInstances: Map<string, BABYLON.TransformNode> = new Map();
    private _assemblyInstanceColors: Map<string, number | null> = new Map();
    private _assemblyInstanceTemplates: Map<string, string> = new Map();
    constructor(viewer: PointViewer, scene: BABYLON.Scene) {
        this._scene = scene;
        this._viewer = viewer;
        this._glbConverter = new GLBConverter();
        this._assemblySelectionMaterial = new BABYLON.StandardMaterial("AssemblySelectionMaterial", scene);
        this._assemblySelectionMaterial.diffuseColor = new BABYLON.Color3(0, .8, .8);
        this._assemblySelectionMaterial.ambientColor = new BABYLON.Color3(0, .8, .8);
        this._assemblySelectionMaterial.emissiveColor = new BABYLON.Color3(0, .2, .2);
        this._assemblySelectionMaterial.specularColor = new BABYLON.Color3(.8, .8, .8);
        this._assemblySelectionMaterial.specularPower = 64;
        this._assemblySelectionMaterial.backFaceCulling = false;
        
        this._scene.onPointerObservable.add(this._handlePointerObservable);
        this._scene.onKeyboardObservable.add(this._handleKeyboardObservable);
        this._canvasMouseLeaveHandler = this._handleMouseLeave.bind(this);
        this._viewer.scene.getEngine().getRenderingCanvas().addEventListener("mouseleave", this._canvasMouseLeaveHandler);
    }
    public dispose() {
        // Remove observables
        this._scene.onPointerObservable.removeCallback(this._handlePointerObservable);
        this._scene.onKeyboardObservable.removeCallback(this._handleKeyboardObservable);

        const canvas = this._viewer.scene.getEngine().getRenderingCanvas();
        canvas.removeEventListener("mouseleave", this._canvasMouseLeaveHandler);

        // Dispose assembly selection material
        if (this._assemblySelectionMaterial) {
            this._assemblySelectionMaterial.dispose();
            this._assemblySelectionMaterial = null;
        }

        // Dispose temp assembly
        this._disposeTempAssembly();

        // Dispose all GLB assemblies and their instances
        for (const meshes of Object.values(this._glbAssemblies)) {
            meshes.forEach((mesh) => mesh.dispose());
        }
        this._glbAssemblies = {};

        for (const source of Object.values(this._graphicSources)) {
            source.dispose();
        }
        this._graphicSources.clear();

        this._glbAssemblyInstances.forEach((instance) => {
            instance.dispose();
        });
        this._glbAssemblyInstances.clear();
        this._assemblyInstanceColors.clear();
        this._assemblyInstanceTemplates.clear();

        this._assemblyColorMaterials.forEach(material => material.dispose());
        this._assemblyColorMaterials.clear();

        // Dispose GLB Converter
        if (this._glbConverter) {
            this._glbConverter = null; // If GLBConverter has a dispose, call it
        }
    }
    public setAssemblyCreationID(assemblyID: string) {
        //this._assemblyCreationID = assemblyID;
    }
    public setAssemblyCreationTemplate(assemblyCreationTemplate: IAssemblyTemplate) {
        this._disposeTempAssembly();
        this._assemblyRotation = 0;
        this._assemblyCreationTemplate = assemblyCreationTemplate;
    }

    public hasAssembly(assemblyID: string, instanceID:string) : boolean {

        if (this._glbAssemblies[assemblyID] != null && this._glbAssemblyInstances.has(instanceID))
            return true;

        return false;
    }

    public async loadGLBAssembly(assemblyID: string, assemblyName:string, glbUrl: string) {

        if (this._glbAssemblies[assemblyID] == null) {
            // Load the GLB and get the meshes
            var glbMeshes = await this._glbConverter.loadGLBUrlIntoScene(this._scene, assemblyName, glbUrl, false);
            glbMeshes.forEach(mesh => mesh.setEnabled(false));
            // Store the filtered meshes in the _glbAssemblies dictionary
            this._glbAssemblies[assemblyID] = glbMeshes as BABYLON.Mesh[];
        }
    }

    public async loadGraphicsources(graphicSources: IGraphicSource[]) {

        graphicSources.forEach(source => this.loadGraphicSource(source.ID, source.Name, source.GraphicsFileUrl, source.Position, source.Rotation, source.Scale));
    }
    public async unloadGraphicSources(graphicSources: IGraphicSource[]) {

        graphicSources.forEach(source => this.unloadGraphicSource(source.ID, source.Name));
    }
    public async unloadGraphicSource(graphicSourceID: string, graphicSourceName: string) {
        if (this._graphicSources.has(graphicSourceID)) {
            console.log("Unloading graphic source " + graphicSourceName);
            this._graphicSources.get(graphicSourceID).dispose();
            this._graphicSources.delete(graphicSourceID);
        }
    }
    public async loadGraphicSource(graphicSourceID: string, graphicSourceName: string, glbUrl: string, position: IVector3D, rotation: number, scaling : number) {

        const graphicSourceURL = glbUrl.startsWith("http")
            ? glbUrl
            : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + glbUrl;

        if (!this._graphicSources.has(graphicSourceID)) {
            console.log("Loading graphic source " + graphicSourceName);
            // Load the GLB and get the meshes
            var glbMeshes = await this._glbConverter.loadGLBUrlIntoScene(this._scene, graphicSourceName, graphicSourceURL, false);

            // To do, set scale, position and rotation.
            var rootTransform = glbMeshes.find(mesh => mesh.name === "__root__");

            // Create a TransformNode as the root for positioning and scaling
            const transformParent = new BABYLON.TransformNode(graphicSourceID, this._scene);
            rootTransform.parent = transformParent;

            transformParent.scaling = new BABYLON.Vector3(scaling, scaling, scaling);
            transformParent.position = new BABYLON.Vector3(position.X, position.Y, position.Z);
            transformParent.rotation = new BABYLON.Vector3(0, rotation, 0);
            
            this._graphicSources.set(graphicSourceID, transformParent);
            //this._graphicSources[graphicSourceID] = glbMeshes as BABYLON.Mesh[];
        }
    }

    public async updateAllAssemblies(assemblies: IAssembly[]) {
        // Delete all assemblies in the instances that are not in assemblies.
        const validInstanceIDs = new Set(assemblies.map(assembly => assembly.ID));
        var selectedMeshID = "";
        if (this._selectedMesh != null) {
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            selectedMeshID = parsedInstanceID;
        }

        this._glbAssemblyInstances.forEach((transformNode, instanceID) => {
            if (!validInstanceIDs.has(instanceID)) {
                console.log(`Deleting assembly instance: ${instanceID}`);

                if (instanceID == selectedMeshID)
                    this._unselectAssembly();

                transformNode.dispose();
                this._glbAssemblyInstances.delete(instanceID);
                this._assemblyInstanceColors.delete(instanceID);
                this._assemblyInstanceTemplates.delete(instanceID);
            }
        });

        await Promise.all(assemblies.map(assembly => this.createOrUpdateAssembly(assembly)));
    }

    public async createOrUpdateAssembly(assembly: IAssembly) {
        var assemblyID = assembly.AssemblyTemplateID;
        var instanceID = assembly.ID;
        var position = new BABYLON.Vector3(assembly.Position.X, assembly.Position.Y, assembly.Position.Z);
        var orientation = new BABYLON.Vector3(assembly.Orientation.X, assembly.Orientation.Y, assembly.Orientation.Z);
        var scaling = new BABYLON.Vector3(assembly.InstanceScale.X, assembly.InstanceScale.Y, assembly.InstanceScale.Z);
        const color = assembly.Color ?? null;

        const existing = this._glbAssemblyInstances.get(instanceID);
        const appearanceChanged = existing && (
            this._assemblyInstanceColors.get(instanceID) !== color
            || this._assemblyInstanceTemplates.get(instanceID) !== assemblyID
        );

        if (appearanceChanged) {
            const wasSelected = this._selectedMesh?.original === existing;
            if (wasSelected) {
                this._unselectAssembly();
                this._selectedMesh = null;
            }
            existing.dispose();
            this._glbAssemblyInstances.delete(instanceID);
            const replacement = await this.createAssemblyInstanceWithHierarchy(assemblyID, instanceID, position, orientation, scaling, true, color);
            if (wasSelected && replacement) {
                this._selectAssembly(replacement);
            }
        } else if (!existing) {
            await this.createAssemblyInstanceWithHierarchy(assemblyID, instanceID, position, orientation, scaling, true, color);
        }
        else {
            this.updateAssembly(instanceID, position, orientation, scaling);
        }
    }

    private updateAssembly(instanceID: string,
        position: BABYLON.Vector3,
        orientation: BABYLON.Vector3,
        scaling: BABYLON.Vector3
    ) {
        var assembly = this._glbAssemblyInstances.get(instanceID);

        assembly.position = position;
        assembly.rotation = orientation;
        assembly.scaling = scaling;
    }

    private async createAssemblyInstanceWithHierarchy(
        assemblyID: string,
        instanceID: string,
        position: BABYLON.Vector3,
        orientation:BABYLON.Vector3,
        scaling: BABYLON.Vector3,
        pickable: boolean,
        color: number | null = null
    ) {
        // When we are adding new instances, dispose the ghost.
        this._disposeTempAssembly();

        if (!this._glbAssemblies[assemblyID]) {

            if (this._assemblyCreationTemplate == null)
                return;

            // assembly is not loaded yet, so now load it!
            const graphicsFileUrl = this._assemblyCreationTemplate.GraphicsFileUrl.startsWith("http")
                ? this._assemblyCreationTemplate.GraphicsFileUrl
                : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + this._assemblyCreationTemplate.GraphicsFileUrl;

            await this.loadGLBAssembly(this._assemblyCreationTemplate.ID, this._assemblyCreationTemplate.Name, graphicsFileUrl);
        }

        const key = JSON.stringify([assemblyID, ':', instanceID]);

        if (this._glbAssemblyInstances.has(instanceID))
            return;

        const originalRoot = this._glbAssemblies[assemblyID].find(mesh => mesh.name === "__root__");
        if (!originalRoot) return null;

        // Create a TransformNode as the root for positioning and scaling
        const rootTransform = new BABYLON.TransformNode(key, this._scene);
        
        // Recursive function to clone hierarchy
        const cloneHierarchy = (childrenToClone: BABYLON.Node[], parentInstance: BABYLON.Node | null) => {
            childrenToClone.forEach(originalMesh => {
                
                // Only create an instance if the mesh is of type BABYLON.Mesh
                if (originalMesh instanceof BABYLON.Mesh) {
                    const instance = color == null
                        ? originalMesh.createInstance(`${instanceID}_${originalMesh.name}`)
                        : originalMesh.clone(`${instanceID}_${originalMesh.name}`, parentInstance, true);
                    if (!instance) return;
                    instance.parent = parentInstance; // Preserve parent-child structure
                    if (color != null) {
                        instance.material = this._getOrCreateAssemblyColorMaterial(color);
                    }
                    instance.setEnabled(true);
                    instance.isPickable = pickable;
                    (instance as any).id = key;
                    instance.id = key;
                    cloneHierarchy(originalMesh.getChildren(), instance);
                } else if (originalMesh instanceof BABYLON.TransformNode) {
                    
                    var transformNodeClone = originalMesh.clone(`${instanceID}_${originalMesh.name}`, parentInstance, true);
                    transformNodeClone.setEnabled(true);
                    cloneHierarchy(originalMesh.getChildren(), transformNodeClone);
                }
                else {
                    console.log("Unsupported Type");
                }
            });
        };

        // Begin cloning from the original root, setting rootTransform as the top parent
        cloneHierarchy(originalRoot.getChildren(), rootTransform);

        rootTransform.position = position;
        rootTransform.rotation = orientation;
        rootTransform.scaling = scaling;

        if (instanceID != "temp") {
            this._glbAssemblyInstances.set(instanceID, rootTransform);
            this._assemblyInstanceColors.set(instanceID, color);
            this._assemblyInstanceTemplates.set(instanceID, assemblyID);
        }

        return rootTransform; // Return the root for external positioning if needed
    }
    public setSelectedAssemblyEdit() {
        this._unselectAssembly();
        this.setPositioningMode();
        this.setRotationMode();
    }
    public setSelectedAssemblyScale() {
        this._unselectAssembly();
        this.setScaleMode();
    }
    private setScaleMode() {
        if (this._selectedMesh == null)
            return;

        this._currentAssemblyRotation = this._selectedMesh.original.rotation.clone();
        var boundingBoxGizmo = new BABYLON.BoundingBoxGizmo(BABYLON.Color3.FromHexString("#0984e3"));
        
        boundingBoxGizmo.attachedMesh = this._selectedMesh.original as any;

        const draggerEnd = (() => {
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            var position: IVector3D = { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z };
            var rotation: IVector3D = { X: this._currentAssemblyRotation.x, Y: this._currentAssemblyRotation.y, Z: this._currentAssemblyRotation.z };
            var scale: IVector3D = { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z };
            ViewerActions.updateAssemblyProperties(parsedInstanceID, position, rotation, scale);
        });

        boundingBoxGizmo.onScaleBoxDragEndObservable.add(draggerEnd);
        boundingBoxGizmo.setEnabledRotationAxis("");
        boundingBoxGizmo.scaleDragSpeed = 1.5;

        boundingBoxGizmo.onScaleBoxDragObservable.add(() => {
            // Ensure scaling values do not go negative
            this._selectedMesh.original.scaling.x = Math.max(0.001, this._selectedMesh.original.scaling.x); 
            this._selectedMesh.original.scaling.y = Math.max(0.001, this._selectedMesh.original.scaling.y);
            this._selectedMesh.original.scaling.z = Math.max(0.001, this._selectedMesh.original.scaling.z);
        });

        this._scaleGizmo = boundingBoxGizmo;
    }
    private setPositioningMode() {
        if (this._selectedMesh == null)
            return;

        var that = this;
        var gizmo = new BABYLON.PositionGizmo();

        gizmo.attachedMesh = this._selectedMesh.original as any;
        const draggerEnd = (() => {
            if (that._rotationGizmo != null) {
                that._rotationGizmo.yGizmo.isEnabled = true;
            }

            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            var position: IVector3D = { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z };
            var rotation: IVector3D = { X: this._selectedMesh.original.rotation.x, Y: this._selectedMesh.original.rotation.y, Z: this._selectedMesh.original.rotation.z };
            var scale: IVector3D = { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z };
            ViewerActions.updateAssemblyProperties(parsedInstanceID, position, rotation, scale);
        });
        const draggerStart = (() => {
            if (that._rotationGizmo != null) {
                that._rotationGizmo.yGizmo.isEnabled = false;
            }
        });
        gizmo.xGizmo.dragBehavior.onDragEndObservable.add(draggerEnd);
        gizmo.yGizmo.dragBehavior.onDragEndObservable.add(draggerEnd);
        gizmo.zGizmo.dragBehavior.onDragEndObservable.add(draggerEnd);

        gizmo.xGizmo.dragBehavior.onDragStartObservable.add(draggerStart);
        gizmo.yGizmo.dragBehavior.onDragStartObservable.add(draggerStart);
        gizmo.zGizmo.dragBehavior.onDragStartObservable.add(draggerStart);
        this._positionGizmo = gizmo;
    }

    private setRotationMode() {
        if (this._selectedMesh == null)
            return;

        var that = this;
        var gizmo = new BABYLON.RotationGizmo();

        gizmo.attachedMesh = this._selectedMesh.original as any;
        const draggerEnd = (() => {
            if (that._positionGizmo != null) {
                that._positionGizmo.xGizmo.isEnabled = true;
                that._positionGizmo.yGizmo.isEnabled = true;
                that._positionGizmo.zGizmo.isEnabled = true;
            }
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            var position: IVector3D = { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z };
            var rotation: IVector3D = { X: this._selectedMesh.original.rotation.x, Y: this._selectedMesh.original.rotation.y, Z: this._selectedMesh.original.rotation.z };
            var scale: IVector3D = { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z };
            ViewerActions.updateAssemblyProperties(parsedInstanceID, position, rotation, scale);
        });

        const draggerStart = (() => {
            if (that._positionGizmo != null) {
                that._positionGizmo.xGizmo.isEnabled = false;
                that._positionGizmo.yGizmo.isEnabled = false;
                that._positionGizmo.zGizmo.isEnabled = false;
            }
        });

        gizmo.xGizmo.isEnabled = false;
        gizmo.zGizmo.isEnabled = false;
        gizmo.yGizmo.isEnabled = true;
        gizmo.yGizmo.dragBehavior.onDragEndObservable.add(draggerEnd);

        gizmo.yGizmo.dragBehavior.onDragStartObservable.add(draggerStart);
        gizmo.scaleRatio = 1.5;
        gizmo.updateGizmoRotationToMatchAttachedMesh = false;
        this._rotationGizmo = gizmo;
    }

    private _disposeTempAssembly() {
        if (this._tempAssembly != null) {
            this._tempAssembly.dispose(false);
            this._tempAssembly = null;
        }
    }
    
    private _unselectAssembly(): void {
        if (this._selectedMesh) {
            this._selectedMesh.original.setEnabled(true);
            this._selectedMesh.selection.dispose();
        }
        if (this._positionGizmo) {
            this._positionGizmo.dispose();
            this._positionGizmo = null;
        }
        if (this._rotationGizmo) {
            this._rotationGizmo.dispose();
            this._rotationGizmo = null;
        }
        if (this._scaleGizmo) {
            this._scaleGizmo.dispose();
            this._scaleGizmo = null;
        }
    }
    public selectAssembly(assemblyID: string): void {

        this._unselectAssembly();
        this._selectedMesh = null;

        if (this._glbAssemblyInstances.has(assemblyID)) {
            this._selectAssembly(this._glbAssemblyInstances.get(assemblyID));
        }
    }

    public getSelectedAssemblyID(): string {
        if (this._selectedMesh) {
            if (this._selectedMesh.original.id.indexOf(':') < 0)
                return null; // Key is not an assembly, so ignore.

            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);

            return parsedInstanceID;
        }

        return null;
    }

    public isSelectedAssembly(id: string) {
        return this._selectedMesh?.selection.id == id;
    }

    public getSelectedAssemblyBounds(assemblyID:string) {
        //var bounds = this._getAssemblyBounds(assemblyID);
        var root = this._glbAssemblyInstances.get(assemblyID);

        const bounds = root.getHierarchyBoundingVectors(true);
        const scaling = root.scaling;

        return { min: { X: bounds.min.x, Y: bounds.min.y, Z: bounds.min.z }, max: { X: bounds.max.x, Y: bounds.max.y, Z: bounds.max.z }, scaling: { X: scaling.x, Y: scaling.y, Z: scaling.z } };
    }
    private _selectAssembly(mesh: BABYLON.TransformNode): void {

        // Assuming mesh.id is the unique key
        const key = mesh.id;

        if (key.indexOf(':') < 0)
            return; // Key is not an assembly, so ignore.

        const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(key);

        // Ensure _glbAssemblies[key] is properly typed
        const originalRoot = this._glbAssemblies[parsedAssemblyID]?.find(node => node.name === "__root__");
        if (!originalRoot) {
            console.warn(`Original root not found for key: ${key}`);
            return;
        }

        // Create a TransformNode as the root for positioning and scaling
        const rootTransform = mesh.clone(key, null, true) as BABYLON.TransformNode;

        // Recursive function to clone hierarchy
        const cloneHierarchy = (childrenToClone: BABYLON.Node[], parentInstance: BABYLON.Node | null): void => {
            childrenToClone.forEach(originalNode => {
                if (originalNode instanceof BABYLON.Mesh) {
                    // Clone the mesh
                    const instance = originalNode.clone(`highlightclone`);
                    if (!instance) return;
                    instance.parent = parentInstance;
                    instance.setEnabled(true);
                    instance.isPickable = true;
                    (instance as any).id = key;

                    instance.renderingGroupId = 1;
                    instance.material = this._assemblySelectionMaterial;
                    // Recursively clone children
                    cloneHierarchy(originalNode.getChildren(), instance);
                } else if (originalNode instanceof BABYLON.TransformNode) {
                    // Clone the transform node
                    const transformNodeClone = originalNode.clone(`highlightclone`, parentInstance, true);
                    transformNodeClone.setEnabled(true);

                    // Recursively clone children
                    cloneHierarchy(originalNode.getChildren(), transformNodeClone);
                } else {
                    console.warn("Unsupported node type:", originalNode);
                }
            });
        };

        // Begin cloning from the original root, setting rootTransform as the top parent
        cloneHierarchy(originalRoot.getChildren(), rootTransform);

        mesh.setEnabled(false);

        this._selectedMesh = { original: mesh, selection: rootTransform };
    }

    private _getOrCreateAssemblyColorMaterial(color: number): BABYLON.StandardMaterial {
        let material = this._assemblyColorMaterials.get(color);
        if (material) return material;

        const hex = `#${(color >>> 0).toString(16).padStart(8, '0')}`;
        const rgba = BABYLON.Color4.FromHexString(hex);
        material = new BABYLON.StandardMaterial(`AssemblyColor_${hex}`, this._scene);
        material.diffuseColor = new BABYLON.Color3(rgba.r, rgba.g, rgba.b);
        material.ambientColor = material.diffuseColor;
        material.emissiveColor = material.diffuseColor.scale(.7);
        material.alpha = rgba.a;
        this._assemblyColorMaterials.set(color, material);
        return material;
    }

    private async _createAssemblyInstance(name: string, position: BABYLON.Vector3, scale:BABYLON.Vector3, rotation:BABYLON.Vector3, assemblyTemplateID: string) {
        var jsonAssembly = { Name: name, AssemblyTemplateID: assemblyTemplateID };

        (jsonAssembly as any).Position = { X: position.x, Y: position.y, Z: position.z };
        (jsonAssembly as any).Orientation = { X: rotation.x, Y: rotation.y, Z: rotation.z };
        (jsonAssembly as any).InstanceScale = { X: scale.x, Y: scale.y, Z: scale.z };
        (jsonAssembly as any).Orientation = { X: rotation.x, Y: rotation.y, Z: rotation.z };

        await ViewerActions.resetDisplayMode();
        await ViewerActions.saveAssemblyInstance(jsonAssembly as any);
    }
    
    private _updateCursorStyle(style: string) {
        const canvas = this._viewer.scene.getEngine().getRenderingCanvas();
        this._currentCursorStyle = style; // Update the stored cursor style
        canvas.style.cursor = style; // Apply the new cursor style to the canvas
    }
    private _handleMouseLeave(event: MouseEvent) {
        this._disposeTempAssembly();
    }
    private _handleKeyboardObservable = async (evt: BABYLON.KeyboardInfo) => {
        switch (evt.type) {
            case BABYLON.KeyboardEventTypes.KEYDOWN:
                switch (evt.event.code) {
                    case "KeyQ":
                        if (this._tempAssembly != null) {
                            this._tempAssembly.rotation.y -= .05;
                            this._assemblyRotation = this._tempAssembly.rotation.y;
                        }
                        break;
                    case "KeyW":
                        if (this._tempAssembly != null) {
                            this._tempAssembly.rotation.y += .05;
                            this._assemblyRotation = this._tempAssembly.rotation.y;
                        }

                        break;
                }
                break;
        }
    };
    private _handlePointerObservable = async (evt: BABYLON.PointerInfo) => {
        const canvas = this._viewer.scene.getEngine().getRenderingCanvas();

        switch (evt.type) {
            case BABYLON.PointerEventTypes.POINTERDOWN:
                this._pointerMoved = false;
                const downPosition = new BABYLON.Vector2(evt.event.clientX, evt.event.clientY);
                this._lastPointerPosition = downPosition; // Record pointer position on down
                break;

            case BABYLON.PointerEventTypes.POINTERUP:
                if (this._pointerMoved) {
                    // Handle logic when the pointer has moved significantly.
                    break;
                }

                if (this._assemblyCreationTemplate != null && this._tempAssembly != null) {
                    await this._createAssemblyInstance(
                        this._assemblyCreationTemplate.Name,
                        this._tempAssembly.position,
                        new BABYLON.Vector3(
                            this._tempAssembly.scaling.x,
                            this._tempAssembly.scaling.y,
                            this._tempAssembly.scaling.z
                        ),
                        this._tempAssembly.rotation,
                        this._assemblyCreationTemplate.ID
                    );

                    canvas.style.cursor = 'auto';
                } else if (this._assemblyCreationTemplate != null && this._tempAssembly == null) {
                    ViewerActions.resetDisplayMode();
                } 
                break;

            case BABYLON.PointerEventTypes.POINTERMOVE:
                if (this._lastPointerPosition) {
                    const currentPosition = new BABYLON.Vector2(evt.event.clientX, evt.event.clientY);
                    const distance = BABYLON.Vector2.Distance(this._lastPointerPosition, currentPosition);

                    if (distance > this._moveThreshold) {
                        this._pointerMoved = true;
                    }
                }

                if (this._assemblyCreationTemplate == null) return;

                this._viewer.scene.getEngine().getRenderingCanvas().focus();
                const canvasRect = this._viewer.scene.getEngine().getRenderingCanvasClientRect();
                const x = evt.event.clientX - canvasRect.left;
                const y = evt.event.clientY - canvasRect.top;
                const screenPoint = new BABYLON.Vector2(x, y);
                const intersection = this._viewer.pointCloudManager.pickPointCloud(screenPoint, this._scene, 0.01);

                if (intersection.valid) {
                    const origin = new BABYLON.Vector3(
                        this._assemblyCreationTemplate.Origin.X,
                        this._assemblyCreationTemplate.Origin.Y,
                        this._assemblyCreationTemplate.Origin.Z
                    );

                    // Since we await, there is a chance that another pointer observable
                    // can trigger at the same time. Therefore, we lock the instance creation.
                    if (!this._tempAssembly && !this._tempAssemblyLock) {
                        this._tempAssemblyLock = true; // Lock the section
                        try {
                            const temporaryAssembly = await this.createAssemblyInstanceWithHierarchy(
                                this._assemblyCreationTemplate.ID,
                                'temp',
                                intersection.point.subtract(origin),
                                new BABYLON.Vector3(0, this._assemblyRotation, 0),
                                new BABYLON.Vector3(
                                    this._assemblyCreationTemplate.Scale,
                                    this._assemblyCreationTemplate.Scale,
                                    this._assemblyCreationTemplate.Scale
                                ),
                                false
                            );
                            this._tempAssembly = temporaryAssembly;
                        } catch (error) {
                            console.error('Error creating tempAssembly:', error);
                        } finally {
                            this._tempAssemblyLock = false; // Release the lock
                        }
                    }

                    if (this._tempAssembly != null)
                        this._tempAssembly.position = intersection.point.subtract(origin);

                    this._updateCursorStyle(
                        "url('" +
                        Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() +
                        "/cursors/validAssembly.png" +
                        "'), auto"
                    );
                } else {
                    this._disposeTempAssembly();
                    this._updateCursorStyle(
                        "url('" +
                        Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() +
                        "/cursors/invalidAssembly.png" +
                        "'), auto"
                    );
                }
                break;
        }
    };
}

export default GLBManager;
